import React, { useState, useEffect } from 'react';
import { 
  Image, ArrowLeft, Package, FileText, Tag, DollarSign, Upload, Save, 
  Sparkles, X, CheckSquare, ChevronDown, ChevronUp, Box, Settings,
  ImageIcon, Plus, Trash2, Loader2
} from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { productAPI, categoryAPI, variationAPI } from '../services/api';
import AdminHeader from '../components/AdminHeader';
import VariationManagement from '../components/VariationManagement';
import ImageGalleryPage from './ImageGalleryPage';
import { compressImage } from '../utils/imageCompression';
import logger from '../utils/logger';
import './EditProductPage.css';

const EditProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    stock: '',
    price: '',
    scientificName: '',
    size: 'Medium',
    environment: '',
    characteristics: [],
    images: [],
    mainImage: '',
    topSelling: false,
    imported: false
  });
  
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [savingCategory, setSavingCategory] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [characteristicsInput, setCharacteristicsInput] = useState('');
  const [variations, setVariations] = useState([]);
  const [hasVariations, setHasVariations] = useState(false);
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [galleryMode, setGalleryMode] = useState('main'); // 'main' or 'gallery'
  const [imageLoading, setImageLoading] = useState(false);
  
  // Collapsible sections state
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    pricing: true,
    inventory: true,
    images: true,
    characteristics: false,
    settings: false,
    variations: true
  });

  useEffect(() => {
    fetchProductData();
    fetchCategories();
    fetchVariations();
  }, [id]);

  const fetchVariations = async () => {
    try {
      const response = await variationAPI.getProductVariations(id);
      const variationsData = response.data.map(v => {
        // Handle attributes - convert Map to object if needed, otherwise use as-is
        let attributes = v.attributes;
        if (attributes instanceof Map) {
          attributes = Object.fromEntries(attributes);
        } else if (Array.isArray(attributes)) {
          attributes = Object.fromEntries(attributes);
        } else if (!attributes || typeof attributes !== 'object') {
          attributes = {};
        }
        
        // Handle metadata - convert Map to object if needed, otherwise use as-is
        let metadata = v.metadata;
        if (metadata instanceof Map) {
          metadata = Object.fromEntries(metadata);
        } else if (Array.isArray(metadata)) {
          metadata = Object.fromEntries(metadata);
        } else if (!metadata || typeof metadata !== 'object') {
          metadata = {};
        }
        
        return {
          ...v,
          attributes,
          metadata
        };
      });
      setVariations(variationsData);
      // Update hasVariations based on actual variations
      if (variationsData.length > 0) {
        setHasVariations(true);
      }
    } catch (err) {
      logger.debug('No variations found or error fetching:', err);
      setVariations([]);
      setHasVariations(false);
    }
  };

  const fetchProductData = async () => {
    try {
      setLoading(true);
      const response = await productAPI.getProduct(id);
      const product = response.data;
      
      const characteristics = product.characteristics || [];
      setFormData({
        name: product.name || '',
        description: product.description || '',
        category: product.category || '',
        stock: product.stock || '',
        price: product.price || '',
        scientificName: product.scientificName || '',
        size: product.size || 'Medium',
        environment: product.environment || '',
        characteristics: characteristics,
        images: product.images || [],
        mainImage: product.mainImage || '',
        topSelling: product.topSelling || false,
        imported: product.imported || false
      });
      
      // Check if product has variations
      setHasVariations(product.hasVariations || false);
      
      setCharacteristicsInput(characteristics.join(', '));
      
      if (product.mainImage) {
        setImagePreview(product.mainImage);
      }
      
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to fetch product data');
      logger.error('Error fetching product:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoryAPI.getCategories();
      const categoryList = response.data || [];
      // Handle both string and object formats and remove pseudo "All"
      const names = categoryList
        .map(cat => (typeof cat === 'string' ? cat : cat.name))
        .filter(name => name && name !== 'All');
      setCategories(names);
    } catch (err) {
      logger.error('Error fetching categories:', err);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files);
    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        handleImageFile(file, true);
      }
    });
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        handleImageFile(file, true);
      }
    });
  };

  const handleImageFile = async (file, isGallery = false) => {
    if (file.size > 8 * 1024 * 1024) {
      alert('File size must be less than 8MB');
      return;
    }

    setImageLoading(true);
    try {
      const compressedBase64 = await compressImage(file, {
        maxWidth: 1920,
        maxHeight: 1920,
        quality: 0.85,
        maxSizeMB: 2
      });
      
      if (isGallery) {
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, compressedBase64]
        }));
      } else {
        setImagePreview(compressedBase64);
        setFormData(prev => ({
          ...prev,
          mainImage: compressedBase64
        }));
      }
    } catch (error) {
      logger.error('Image compression error:', error);
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result;
        if (isGallery) {
          setFormData(prev => ({
            ...prev,
            images: [...prev.images, base64]
          }));
        } else {
          setImagePreview(base64);
          setFormData(prev => ({
            ...prev,
            mainImage: base64
          }));
        }
      };
      reader.readAsDataURL(file);
    } finally {
      setImageLoading(false);
    }
  };

  const handleMainImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleImageFile(file, false);
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const setAsMainImage = (image) => {
    setImagePreview(image);
    setFormData(prev => ({
      ...prev,
      mainImage: image
    }));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleCharacteristicsChange = (e) => {
    const value = e.target.value;
    setCharacteristicsInput(value);
    
    const characteristicsArray = value
      .split(',')
      .map(item => item.trim())
      .filter(item => item);
    
    setFormData(prev => ({
      ...prev,
      characteristics: characteristicsArray
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Price is only required if product doesn't have variations
    const priceRequired = !hasVariations && variations.length === 0;
    
    if (!formData.name.trim() || (priceRequired && !formData.price) || !formData.category) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      
      // Normalize data before sending to backend
      const updateData = {
        ...formData,
        // Convert empty optional fields to null/undefined so Mongoose enum validation doesn't get ''
        environment: formData.environment || null,
        characteristics: Array.isArray(formData.characteristics) 
          ? formData.characteristics 
          : (formData.characteristics || '').split(',').map(item => item.trim()).filter(item => item)
      };
      
      await productAPI.updateProduct(id, updateData);
      alert('Product updated successfully!');
      navigate('/admin/products');
    } catch (err) {
      let errorMessage = 'Failed to update product';
      
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMessage = 'Request timed out. This may be due to slow network connection or large image upload. Please try again with a smaller image or check your network connection.';
      } else {
        errorMessage += ': ' + (err.response?.data?.message || err.message || 'Unknown error');
      }
      
      logger.error('Product update error:', err);
      alert(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="edit-product-page">
        <AdminHeader />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading product data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="edit-product-page">
        <AdminHeader />
        <div className="error-container">
          <p>Error: {error}</p>
          <button onClick={() => navigate('/admin/products')} className="back-btn">
            Back to Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-product-page">
      <AdminHeader />

      <div className="edit-content-wrapper">
        <div className="edit-container">
          {/* Header */}
          <div className="edit-header-section">
            <button 
              className="back-button"
              onClick={() => navigate('/admin/products')}
            >
              <ArrowLeft size={20} />
              Back
            </button>
            <div className="header-title">
              <Package size={24} />
              <h1>Edit Product</h1>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="edit-form">
            {/* Basic Details Section */}
            <SectionCard
              title="Basic Details"
              icon={<FileText size={20} />}
              expanded={expandedSections.basic}
              onToggle={() => toggleSection('basic')}
            >
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    Product Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter product name"
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Scientific Name</label>
                  <input
                    type="text"
                    name="scientificName"
                    value={formData.scientificName}
                    onChange={handleInputChange}
                    placeholder="Enter scientific name (optional)"
                    className="form-input"
                  />
                </div>

                <div className="form-group full-width">
                  <label className="form-label">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Enter product description..."
                    className="form-textarea"
                    rows={5}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Category <span className="required">*</span>
                  </label>
                  <div className="category-input-row">
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className="form-select"
                      required
                    >
                      <option value="">Select category</option>
                      {categories.map(category => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => setShowCategoryModal(true)}
                      style={{ marginLeft: '0.75rem', whiteSpace: 'nowrap' }}
                    >
                      + New Category
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Size</label>
                  <select
                    name="size"
                    value={formData.size}
                    onChange={handleInputChange}
                    className="form-select"
                  >
                    <option value="Micro">Micro</option>
                    <option value="Small">Small</option>
                    <option value="Medium">Medium</option>
                    <option value="Large">Large</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Characteristics</label>
                  <input
                    type="text"
                    value={characteristicsInput}
                    onChange={handleCharacteristicsChange}
                    placeholder="e.g., Low Maintenance, Evergreen (comma separated)"
                    className="form-input"
                  />
                  {formData.characteristics.length > 0 && (
                    <div className="characteristics-tags" style={{ marginTop: '0.5rem' }}>
                      {formData.characteristics.map((char, index) => (
                        <span key={index} className="characteristic-tag">
                          {char}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Environment</label>
                  <select
                    name="environment"
                    value={formData.environment}
                    onChange={handleInputChange}
                    className="form-select"
                  >
                    <option value="">Select environment (optional)</option>
                    <option value="Indoor">Indoor</option>
                    <option value="Outdoor">Outdoor</option>
                  </select>
                </div>
              </div>
            </SectionCard>

            {/* Pricing & Inventory Section */}
            <SectionCard
              title="Pricing & Inventory"
              icon={<DollarSign size={20} />}
              expanded={expandedSections.pricing}
              onToggle={() => toggleSection('pricing')}
            >
              {hasVariations || variations.length > 0 ? (
                <div className="variations-disabled-notice">
                  <p className="notice-message">
                    <strong>Pricing and inventory are managed per variation.</strong>
                    <br />
                    Since this product has variations, price and stock are set individually for each variation below.
                    The product's base price will automatically match the default variation's price.
                  </p>
                </div>
              ) : (
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">
                      Price <span className="required">*</span>
                    </label>
                    <div className="input-with-prefix">
                      <span className="input-prefix">₱</span>
                      <input
                        type="number"
                        name="price"
                        value={formData.price}
                        onChange={handleInputChange}
                        placeholder="0.00"
                        className="form-input"
                        min="0"
                        step="0.01"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Stock Quantity</label>
                    <input
                      type="number"
                      name="stock"
                      value={formData.stock}
                      onChange={handleInputChange}
                      placeholder="0"
                      className="form-input"
                      min="0"
                    />
                    <p className="form-hint">
                      Total available stock for this product
                    </p>
                  </div>
                </div>
              )}
            </SectionCard>

            {/* Images Section */}
            <SectionCard
              title="Product Images"
              icon={<ImageIcon size={20} />}
              expanded={expandedSections.images}
              onToggle={() => toggleSection('images')}
            >
              <div className="images-section">
                {/* Main Image */}
                <div className="main-image-section">
                  <label className="section-subtitle">Main Image</label>
                  {imagePreview ? (
                    <div className="main-image-preview">
                      <img src={imagePreview} alt="Main product" />
                      <div className="image-actions">
                        <button
                          type="button"
                          onClick={() => {
                            setGalleryMode('main');
                            setShowImageGallery(true);
                          }}
                          className="image-action-btn"
                          title="Select from gallery"
                        >
                          <ImageIcon size={16} />
                          From Gallery
                        </button>
                        <label className="image-action-btn">
                          <Upload size={16} />
                          Upload
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleMainImageSelect}
                            style={{ display: 'none' }}
                            disabled={imageLoading}
                          />
                        </label>
                        <button
                          type="button"
                          onClick={() => {
                            setImagePreview(null);
                            setFormData(prev => ({ ...prev, mainImage: '' }));
                          }}
                          className="image-action-btn"
                        >
                          <X size={16} />
                          Remove
                        </button>
                      </div>
                      {imageLoading && (
                        <div className="image-loading-overlay">
                          <Loader2 className="spinning" size={24} />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="image-upload-options">
                      <label className="image-upload-area">
                        <ImageIcon size={32} />
                        <span>Upload Main Image</span>
                        {imageLoading && <Loader2 className="spinning" size={20} />}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleMainImageSelect}
                          style={{ display: 'none' }}
                          disabled={imageLoading}
                        />
                      </label>
                      <div className="upload-divider">OR</div>
                      <button
                        type="button"
                        onClick={() => {
                          setGalleryMode('main');
                          setShowImageGallery(true);
                        }}
                        className="gallery-select-btn"
                      >
                        <ImageIcon size={20} />
                        Select from Gallery 🌿
                      </button>
                    </div>
                  )}
                </div>

                {/* Gallery */}
                <div className="gallery-section">
                  <label className="section-subtitle">Image Gallery</label>
                  <div className="gallery-upload-section">
                    <div 
                      className={`gallery-drop-zone ${dragActive ? 'drag-active' : ''}`}
                      onDrop={handleImageDrop}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onClick={() => document.getElementById('gallery-upload').click()}
                    >
                      <Upload size={24} />
                      <p>Drop images here or click to browse</p>
                      <span className="file-hint">JPG, PNG (max 8MB each)</span>
                      <input
                        id="gallery-upload"
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageSelect}
                        style={{ display: 'none' }}
                        disabled={imageLoading}
                      />
                    </div>
                    <div className="gallery-divider">OR</div>
                    <button
                      type="button"
                      onClick={() => {
                        setGalleryMode('gallery');
                        setShowImageGallery(true);
                      }}
                      className="gallery-select-btn"
                    >
                      <ImageIcon size={20} />
                      Select from Gallery 🌿
                    </button>
                  </div>

                  {formData.images.length > 0 && (
                    <div className="gallery-grid">
                      {formData.images.map((img, index) => (
                        <div key={index} className="gallery-item">
                          <img src={img} alt={`Gallery ${index + 1}`} />
                          <div className="gallery-item-actions">
                            {img !== imagePreview && (
                              <button
                                type="button"
                                onClick={() => setAsMainImage(img)}
                                className="gallery-action-btn"
                                title="Set as main"
                              >
                                <ImageIcon size={14} />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => removeImage(index)}
                              className="gallery-action-btn delete"
                              title="Remove"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                          {img === imagePreview && (
                            <div className="main-badge">Main</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </SectionCard>

            {/* Variations Section */}
            <SectionCard
              title="Product Variations"
              icon={<Box size={20} />}
              expanded={expandedSections.variations}
              onToggle={() => toggleSection('variations')}
            >
              <VariationManagement
                productId={id}
                variations={variations}
                onVariationsChange={(updatedVariations) => {
                  setVariations(updatedVariations);
                }}
              />
            </SectionCard>

            {/* Additional Settings Section */}
            <SectionCard
              title="Additional Settings"
              icon={<Settings size={20} />}
              expanded={expandedSections.settings}
              onToggle={() => toggleSection('settings')}
            >
              <div className="settings-grid">
                <label className="setting-checkbox">
                  <input
                    type="checkbox"
                    name="topSelling"
                    checked={formData.topSelling}
                    onChange={handleInputChange}
                  />
                  <span className="checkbox-label">
                    <span className="checkbox-title">Top Selling</span>
                    <span className="checkbox-desc">Mark as best seller</span>
                  </span>
                </label>

                <label className="setting-checkbox">
                  <input
                    type="checkbox"
                    name="imported"
                    checked={formData.imported}
                    onChange={handleInputChange}
                  />
                  <span className="checkbox-label">
                    <span className="checkbox-title">Imported Product</span>
                    <span className="checkbox-desc">Show in imported section</span>
                  </span>
                </label>
              </div>
            </SectionCard>

            {/* Submit Button */}
            <div className="form-actions">
              <button 
                type="button"
                onClick={() => navigate('/admin/products')}
                className="cancel-button"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="save-button"
                disabled={saving}
              >
                <Save size={18} />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Image Gallery Modal */}
      {showImageGallery && (
        <ImageGalleryPage
          isModal={true}
          allowMultiSelect={galleryMode === 'gallery'}
          onClose={() => setShowImageGallery(false)}
          onSelectImage={(imageUrl) => {
            if (galleryMode === 'main') {
              setImagePreview(imageUrl);
              setFormData(prev => ({ ...prev, mainImage: imageUrl }));
            } else {
              setFormData(prev => ({ ...prev, images: [...prev.images, imageUrl] }));
            }
            setShowImageGallery(false);
          }}
          onSelectImages={(imageUrls) => {
            if (galleryMode === 'gallery') {
              setFormData(prev => ({ ...prev, images: [...prev.images, ...imageUrls] }));
            }
            setShowImageGallery(false);
          }}
        />
      )}

      {/* Add Category Modal (same UX as AddProductPage) */}
      {showCategoryModal && (
        <div className="modal-overlay">
          <div className="bulk-modal">
            <div className="modal-header">
              <h3>Add New Category</h3>
              <button
                className="close-btn"
                type="button"
                onClick={() => {
                  if (!savingCategory) {
                    setShowCategoryModal(false);
                    setNewCategory('');
                  }
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <label className="form-label">Category name</label>
              <input
                type="text"
                className="form-input"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder="e.g., Succulents"
                disabled={savingCategory}
              />
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={() => {
                  if (!savingCategory) {
                    setShowCategoryModal(false);
                    setNewCategory('');
                  }
                }}
                disabled={savingCategory}
              >
                Cancel
              </button>
              <button
                type="button"
                className="execute-btn"
                disabled={savingCategory}
                onClick={async () => {
                  const name = newCategory.trim();
                  if (!name) {
                    alert('Please enter a category name before saving.');
                    return;
                  }
                  try {
                    setSavingCategory(true);
                    await categoryAPI.createCategory({ name });
                    setCategories(prev =>
                      prev.includes(name) ? prev : [...prev, name]
                    );
                    setFormData(prev => ({ ...prev, category: name }));
                    setShowCategoryModal(false);
                    setNewCategory('');
                  } catch (err) {
                    logger.error('Error creating category from EditProductPage modal:', err);
                    alert(
                      err?.response?.data?.message ||
                        'Failed to create category. Please try again.'
                    );
                  } finally {
                    setSavingCategory(false);
                  }
                }}
              >
                {savingCategory ? 'Saving...' : 'Save Category'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Collapsible Section Component
const SectionCard = ({ title, icon, expanded, onToggle, children }) => {
  return (
    <div className="section-card">
      <button
        type="button"
        className="section-header"
        onClick={onToggle}
      >
        <div className="section-header-left">
          {icon}
          <h3>{title}</h3>
        </div>
        {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </button>
      {expanded && (
        <div className="section-content">
          {children}
        </div>
      )}
    </div>
  );
};

export default EditProductPage;
